// =============================================================================
// MeuDriver — AchievementBadge
// Badge individual de conquista: ícone + título + estado earned/locked
// =============================================================================

import { cn } from '@/lib/utils'
import type { AchievementWithProgress } from '@/hooks/useAchievements'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'

interface Props {
  achievement: AchievementWithProgress
  size?: 'sm' | 'md' | 'lg'
  showTooltip?: boolean
}

export function AchievementBadge({ achievement, size = 'md', showTooltip = true }: Props) {
  const earned = achievement.earned

  const sizeClasses = {
    sm: 'h-14 w-14 text-xl',
    md: 'h-20 w-20 text-3xl',
    lg: 'h-24 w-24 text-4xl',
  }

  const labelSize = {
    sm: 'text-[10px]',
    md: 'text-xs',
    lg: 'text-sm',
  }

  const tooltipText = earned
    ? `Conquistado em ${achievement.earned_at ? format(parseISO(achievement.earned_at), "d 'de' MMMM 'de' yyyy", { locale: ptBR }) : '—'}`
    : `Necessário: ${achievement.condition_value} ${getConditionLabel(achievement.condition_type)}`

  return (
    <div
      className="flex flex-col items-center gap-1.5 group"
      title={showTooltip ? tooltipText : undefined}
      data-testid={`achievement-badge-${achievement.slug}`}
    >
      <div
        className={cn(
          'relative flex items-center justify-center rounded-2xl border-2 transition-all duration-300',
          sizeClasses[size],
          earned
            ? 'border-brand-300 bg-gradient-to-br from-brand-50 to-teal-50 shadow-md group-hover:shadow-lg group-hover:scale-105'
            : 'border-gray-200 bg-gray-50 grayscale opacity-50',
        )}
      >
        <span role="img" aria-label={achievement.title}>
          {achievement.icon}
        </span>
        {earned && (
          <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-brand-600 text-white text-[10px] font-bold shadow">
            +{achievement.xp_reward}
          </span>
        )}
      </div>
      <div className="text-center max-w-[80px]">
        <p
          className={cn(
            'font-semibold leading-tight truncate w-full',
            labelSize[size],
            earned ? 'text-gray-800' : 'text-gray-400',
          )}
          title={achievement.title}
        >
          {achievement.title}
        </p>
      </div>
    </div>
  )
}

function getConditionLabel(type: string): string {
  switch (type) {
    case 'lessons_count':   return 'aulas concluídas'
    case 'streak_days':     return 'semanas seguidas'
    case 'quiz_score':      return '% de acerto no simulado'
    case 'rating_given':    return 'avaliações feitas'
    case 'referral_count':  return 'indicações'
    default:                return ''
  }
}
